import { useState, memo, useMemo } from 'react'

import isEqual from 'react-fast-compare'
import {
  BarChart as RechartsBarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import { AnalyticsResponse } from '@tribeplatform/gql-client/types'

import type { GroupBy, ChartTimeFrame } from '../types/index.js'
import {
  DefaultChartColors,
  BAR_INACTIVE_OPACITY,
  CHART_ANIMATION_ACTIVE,
  CHART_ANIMATION_DURATION,
} from '../utils/constants.js'
import {
  analyticsNumberFormatter,
  rechartsLineChartParser as rechartsBarChartParser,
} from '../utils/parser.js'

import { CustomLegend } from './CustomLegend.js'
import { CustomToolTipRechartsHOC } from './CustomToolTipRecharts.js'

interface Props {
  data: {
    chartData: AnalyticsResponse
    timeFrame: ChartTimeFrame
    indexBy: string[]
    labels: string[]
    groupBy?: GroupBy[]
    ColorTheme?: string[]
    syncId?: string
  }
}

export const BarChartRecharts = memo(
  ({
    data: {
      chartData,
      timeFrame,
      indexBy,
      labels,
      groupBy,
      ColorTheme = DefaultChartColors,
      syncId,
    },
  }: Props) => {
    const [activeIndex, setActiveIndex] = useState(-1)

    const getOpacity = (index: number) => {
      if (activeIndex === -1) {
        return 1
      }

      if (index === activeIndex) {
        return 1
      }

      return BAR_INACTIVE_OPACITY
    }

    const parsedData = useMemo(() => {
      return rechartsBarChartParser(
        chartData,
        indexBy,
        timeFrame,
        groupBy,
      ) as Record<string, string | number>[]
    }, [chartData, indexBy, timeFrame, groupBy])

    // Calculate dynamic Y-axis width based on the maximum value
    const yAxisWidth = useMemo(() => {
      // Find the maximum value across all data points and all indexBy keys
      let maxValue = 0
      parsedData.forEach(dataPoint => {
        indexBy.forEach(key => {
          const value = dataPoint[key]
          if (typeof value === 'number' && value > maxValue) {
            maxValue = value
          }
        })
      })

      // Format the maximum value to see how wide it will be
      const formattedMaxValue = analyticsNumberFormatter(maxValue)

      // Estimate text width (approximate 8px per character for 12px font)
      // Add some padding (20px) for safety
      const estimatedWidth = formattedMaxValue.length * 10 + 20

      // Set minimum width to prevent too narrow axis
      return Math.min(estimatedWidth, 39)
    }, [indexBy, parsedData])

    return (
      <>
        <CustomLegend
          labels={labels}
          colors={ColorTheme}
          className="mt-2"
          setActiveIndex={setActiveIndex}
        />
        <ResponsiveContainer width="99%" height={320}>
          <RechartsBarChart
            data={parsedData}
            margin={{ top: 20, left: 0, right: 0, bottom: 20 }}
            syncId={syncId}
          >
            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              stroke="#EAF0F4"
            />
            <XAxis
              tick={{ fontSize: 12 }}
              tickMargin={12}
              dataKey="date"
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={0}
              width={yAxisWidth}
              // fontSize is being overridden by tribe's global selectors
              // so we have to use style here to set the fontsize
              // tick={{ fontSize: 12 }}
              style={{ fontSize: 12 }}
              tickFormatter={value => analyticsNumberFormatter(value)}
            />
            <Tooltip
              content={CustomToolTipRechartsHOC({ labels })}
              cursor={{
                stroke: '#C6C6D2',
                strokeDasharray: '5 5',
                fill: 'transparent',
              }}
            />
            {indexBy.map((label, index) => (
              <Bar
                key={label}
                type="linear"
                dataKey={label}
                fill={ColorTheme[index]}
                isAnimationActive={CHART_ANIMATION_ACTIVE}
                animationDuration={CHART_ANIMATION_DURATION}
                stackId="a"
                opacity={getOpacity(index)}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </>
    )
  },
  isEqual,
)
